import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import {
  getAdminProfile,
  updateAdminProfile,
  uploadAdminAvatar,
} from "../../../api/services/services";
import { getImageUrl } from "../../../api/config/apiConfig";
import "./style.css";

const ProfileModal = ({ open, onClose, onProfileUpdated }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [avatar, setAvatar] = useState("");
  const [avatarPreview, setAvatarPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      loadProfile();
    }
  }, [open]);

  const loadProfile = () => {
    setLoading(true);
    setMessage({ text: "", type: "" });

    getAdminProfile()
      .then((res) => {
        const data = res?.data?.data;
        if (data) {
          setName(data.name || "");
          setPhone(data.phone || "");
          setEmail(data.email || "");
          setAvatar(data.avatar || "");
          if (data.avatar) {
            setAvatarPreview(getImageUrl(data.avatar));
          }
        }
      })
      .catch(() => {
        // Fallback to local storage
        try {
          const localUser = JSON.parse(localStorage.getItem("shelUserData") || "{}");
          setName(localUser.name || "");
          setEmail(localUser.email || "");
          setPhone(localUser.phone || "");
        } catch (_) {}
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show local preview
    const previewUrl = URL.createObjectURL(file);
    setAvatarPreview(previewUrl);
    setUploadingAvatar(true);

    const formData = new FormData();
    formData.append("avatar", file);

    try {
      const res = await uploadAdminAvatar(formData);
      const resData = res?.data?.data;
      if (resData?.avatarKey) {
        const fullUrl = resData.avatarUrl || getImageUrl(resData.avatarKey);
        setAvatar(resData.avatarKey);
        setAvatarPreview(fullUrl);

        // Immediately update localStorage and broadcast profileUpdated event
        try {
          const localUser = JSON.parse(localStorage.getItem("shelUserData") || "{}");
          const updatedLocal = {
            ...localUser,
            avatar: resData.avatarKey,
          };
          localStorage.setItem("shelUserData", JSON.stringify(updatedLocal));
          window.dispatchEvent(new CustomEvent("profileUpdated", { detail: updatedLocal }));
        } catch (_) {}
      }
      setMessage({ text: "Profile picture updated successfully!", type: "success" });
    } catch (err) {
      setMessage({ text: "Failed to upload profile picture.", type: "error" });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setMessage({ text: "Please enter your name.", type: "error" });
      return;
    }

    setSaving(true);
    setMessage({ text: "", type: "" });

    try {
      const payload = {
        name: name.trim(),
        phone: phone.trim(),
        avatar: avatar || undefined,
      };

      const res = await updateAdminProfile(payload);
      const updated = res?.data?.data;

      // Update localStorage user info and broadcast event
      try {
        const localUser = JSON.parse(localStorage.getItem("shelUserData") || "{}");
        const updatedLocal = {
          ...localUser,
          name: name.trim(),
          phone: phone.trim(),
          avatar: avatar || localUser.avatar,
        };
        localStorage.setItem("shelUserData", JSON.stringify(updatedLocal));
        window.dispatchEvent(new CustomEvent("profileUpdated", { detail: updatedLocal }));
      } catch (_) {}

      setMessage({ text: "Profile details updated successfully!", type: "success" });
      if (onProfileUpdated) {
        onProfileUpdated(updated || { name, phone, avatar });
      }

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      setMessage({ text: "Failed to update profile details.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const initials = name ? name.charAt(0).toUpperCase() : "A";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: "profile-modal-paper" }}
      maxWidth="sm"
      fullWidth
    >
      <div className="profile-modal-header">
        <div className="profile-modal-title">
          <AccountCircleRoundedIcon style={{ color: "#6366f1", fontSize: 24 }} />
          Admin Profile & Personalisation
        </div>
        <IconButton size="small" onClick={onClose}>
          <CloseRoundedIcon style={{ fontSize: 20 }} />
        </IconButton>
      </div>

      <DialogContent style={{ padding: "0 20px" }}>
        {message.text && (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: "8px",
              marginBottom: "14px",
              fontSize: "13px",
              fontWeight: "600",
              backgroundColor: message.type === "success" ? "#ecfdf5" : "#fef2f2",
              color: message.type === "success" ? "#065f46" : "#991b1b",
              border: `1px solid ${message.type === "success" ? "#a7f3d0" : "#fecaca"}`,
            }}
          >
            {message.text}
          </div>
        )}

        <div className="profile-avatar-container">
          <div className="profile-avatar-wrapper">
            {avatarPreview ? (
              <img
                src={avatarPreview}
                alt="Avatar"
                className="profile-avatar-img"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  setAvatarPreview("");
                }}
              />
            ) : (
              <div className="profile-avatar-placeholder">{initials}</div>
            )}
            <label className="profile-camera-badge" title="Change Avatar">
              <CameraAltRoundedIcon style={{ fontSize: 16 }} />
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleAvatarChange}
                disabled={uploadingAvatar}
              />
            </label>
          </div>
          {uploadingAvatar && (
            <span style={{ fontSize: "11px", color: "#6366f1", marginTop: "4px" }}>
              Uploading avatar...
            </span>
          )}
        </div>

        <div className="profile-form-group">
          <label className="profile-form-label">Full Name</label>
          <input
            type="text"
            className="profile-form-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter your full name"
            disabled={loading || saving}
          />
        </div>

        <div className="profile-form-group">
          <label className="profile-form-label">Phone Number</label>
          <input
            type="tel"
            className="profile-form-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 234 567 8900"
            disabled={loading || saving}
          />
        </div>

        <div className="profile-form-group">
          <label className="profile-form-label">Email Address (Read-only)</label>
          <input
            type="email"
            className="profile-form-input"
            value={email}
            disabled
          />
        </div>
      </DialogContent>

      <div className="profile-modal-actions">
        <button
          className="profile-btn-cancel"
          onClick={onClose}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          className="profile-btn-save"
          onClick={handleSave}
          disabled={saving || loading}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </Dialog>
  );
};

export default ProfileModal;
